import { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { IndexedStrings } from '../../components/SelectableTable/SelectableTable.types';
import { IPageInfo } from '../../models/page.types';
import { SessionListItem, SessionListsResponse, SessionSortBy } from '../../models/sessionListItem.types';
import { SourceSystemType } from '../../models/examSessionDto.types';
import FilterStateGroup from '../../models/filterStateGroup.types';
import { AxiosError } from 'axios';
import { getSessionsApi } from '../../services/exams/sessions/sessions.service';
import { getErrorPageUrl } from '../../utils/routerUrls';
import { useShowLoader } from '../../Context/Layout.context';
import { ExamSheetConfigured } from '../../models/examSheetConfigured.types';
import { SourceSystems } from '../../models/sourceSystems.types';
import { Months } from '../../models/months.types';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { filtersActions } from '../../reducers/filtersReducer/filtersSlice';
import { DataInfoType, defaultSessionsFilter } from '../../reducers/filtersReducer/filterSlice.types';
import { RootState } from '../../store';
import { getFormattedDateRangeValue } from '../../utils/dateFunctionUtils';

export const useSessionsTableData = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { sessionId } = useParams() as {
    sessionId: string;
  };

  const dataInfoType = DataInfoType.SessionsInfo;
  const sessionsDataInfo = useSelector((state: RootState) => state.filters[dataInfoType]);
  const showLoader = useShowLoader();

  const [filter, setFilter] = useState<IndexedStrings>(sessionsDataInfo.filterData.filter);
  const [isValidFilter, setIsValidFilter] = useState<boolean>(true);

  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [selectedSoureSystem, setSelectedSourceSystem] = useState<SourceSystemType>();
  const [selectedSheetCode, setSelectedSheetCode] = useState('');
  const [selectedSheetId, setSelectedSheetId] = useState<string>('');
  const [selectedSystem, setSelectedSystem] = useState<string>();

  const [filterState, setFilterState] = useState<FilterStateGroup[]>([]);
  const [monthFilter, setMonthFilter] = useState<string[]>(sessionsDataInfo.filterData.filter.month);
  const [sourceSystemTypeFilter, setSourceSystemTypeFilter] = useState<string[]>(
    sessionsDataInfo.filterData.filter.sourceSystemType,
  );
  const [isConfiguredFilter, setIsConfiguredFilter] = useState<string[]>(
    sessionsDataInfo.filterData.filter.isConfigured,
  );
  const [stateFilter, setStateFilter] = useState<string[]>(sessionsDataInfo.filterData.filter.state);
  const [archiveFromFilter, setArchiveFromFilter] = useState<string>(sessionsDataInfo.filterData.filter.archiveFrom);
  const [archiveToFilter, setArchiveToFilter] = useState<string>(sessionsDataInfo.filterData.filter.archiveTo);

  const [sortByColumnId, setSortByColumnId] = useState<SessionSortBy>(sessionsDataInfo.filterData.sort.sortByColumnId);
  const [isDescending, setIsDescending] = useState<boolean>(sessionsDataInfo.filterData.sort.isDescending);

  const getPages = useCallback(
    (_page?: number, _newPageSize?: number) => {
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            pageInfo: {
              ...sessionsDataInfo.filterData.pageInfo,
              currentPage: _page ? _page : 0,
              pageSize: _newPageSize ? _newPageSize : sessionsDataInfo.filterData.pageInfo.pageSize,
            },
          },
        }),
      );
    },
    [filter, sessionsDataInfo.filterData.pageInfo],
  );

  const handleCurrentPage = (response: IPageInfo) => {
    if (response.totalCount < sessionsDataInfo.filterData.pageInfo.pageSize) {
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            pageInfo: {
              ...response,
              currentPage: 0,
            },
          },
        }),
      );
    } else {
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            pageInfo: {
              ...response,
            },
          },
        }),
      );
    }
  };

  const getSortColumnId = (sortColumnId: SessionSortBy): string => {
    if (sortColumnId === 'sourceSystemType') return 'sourceSystemName';
    if ((sortColumnId as string) === 'state') return 'hasNonClosedAssessmentSessions';
    if ((sortColumnId as string) === 'archive') return 'archiveAfter';
    return sortColumnId as string;
  };

  const getSessions = (silent?: boolean, signal?: AbortSignal) => {
    const filters = Object.keys(filter).map((key) => {
      if (Array.isArray(filter[key])) {
        return (filter[key] as string[]).join(',');
      }
      return filter[key];
    });

    if (!isValidFilter) return;

    showLoader(!silent);

    getSessionsApi(
      signal ?? undefined,
      sessionsDataInfo.filterData.pageInfo.pageSize,
      sessionsDataInfo.filterData.pageInfo.currentPage,
      getSortColumnId(sortByColumnId),
      isDescending,
      ...(filters as string[]),
    )
      .then(
        (result: SessionListsResponse) => {
          result.sessionList.forEach((session: SessionListItem) => {
            session.sessionId = Math.floor(Math.random() * 1000000).toString();
          });
          if (result) {
            setSessions(result.sessionList);
          }
          if (result.page) {
            handleCurrentPage(result.page);
          }
        },
        (error: AxiosError) => {
          if (error?.message !== 'canceled') history.push(getErrorPageUrl(error?.code ?? 500, 'admin'));
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };

  useEffect(() => {
    const state: FilterStateGroup[] = [];
    if (Object.values(filter).some((e) => (_.isArray(e) ? e.length > 0 : e !== ''))) {
      state.push({
        name: '',
        checkedItems: [],
      });
    }
    if (filter.month.length > 0) {
      const monthsNames = [...filter.month].map((num: string) => Months[parseInt(num) - 1]);
      state.push({ name: 'Miesiąc:', checkedItems: [...monthsNames] });
    }
    if (filter.sourceSystemType.length > 0) {
      const sourceSystemTypeNames = [...filter.sourceSystemType].map((num: string) => SourceSystems[parseInt(num) - 1]);
      state.push({ name: 'System źródłowy:', checkedItems: [...sourceSystemTypeNames] });
    }
    if (filter.isConfigured.length > 0) {
      const isConfiguredNames = [...filter.isConfigured].map((num: string) => ExamSheetConfigured[parseInt(num) - 1]);
      state.push({ name: 'Konfiguracja arkusza:', checkedItems: [...isConfiguredNames] });
    }
    if (filter.archiveFrom?.length > 0) {
      state.push({
        name: 'Archiwum:',
        checkedItems: [getFormattedDateRangeValue(filter.archiveFrom.toString(), filter.archiveTo.toString())],
      });
    }
    if (filter.state?.length > 0) {
      const arr = filter.state as string[];
      const statusArr: string[] = [];
      for (let i = 0; i < arr.length; i++) {
        if (arr[i] === '2') statusArr.push('Wszystkie sesje zamknięte');
        if (arr[i] === '1') statusArr.push('Nie wszystkie sesje zamknięte');
      }
      state.push({
        name: 'Stan:',
        checkedItems: [...statusArr],
      });
    }
    setFilterState(state);
  }, [filter]);

  useEffect(() => {
    getSessions();
  }, [
    sessionsDataInfo.filterData.pageInfo.currentPage,
    sessionsDataInfo.filterData.pageInfo.pageSize,
    filter,
    isDescending,
    sortByColumnId,
  ]);

  const uncheckAllFilters = useCallback(() => {
    setFilterState([]);
    setFilter(defaultSessionsFilter);
    dispatch(filtersActions.resetFilter(dataInfoType));
  }, [dataInfoType, dispatch]);

  const handleUncheckMonthItem = useCallback(
    (value: string) => {
      const val = monthFilter.some((f) => Months[parseInt(f) - 1] === value)
        ? monthFilter.filter((f) => parseInt(f) !== Months.indexOf(value) + 1)
        : [...monthFilter, (Months.indexOf(value) + 1).toString()];
      setMonthFilter(val);
      setFilter((state) => ({
        ...state,
        month: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              month: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [monthFilter],
  );

  const handleUncheckSourceSystemTypeItem = useCallback(
    (value: string) => {
      const val = sourceSystemTypeFilter.some((f) => SourceSystems[parseInt(f) - 1] === value)
        ? sourceSystemTypeFilter.filter((f) => parseInt(f) !== SourceSystems.indexOf(value) + 1)
        : [...sourceSystemTypeFilter, (SourceSystems.indexOf(value) + 1).toString()];
      setSourceSystemTypeFilter(val);
      setFilter((state) => ({
        ...state,
        sourceSystemType: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              sourceSystemType: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [sourceSystemTypeFilter],
  );

  const handleUncheckArchiveItem = useCallback(
    (value: string) => {
      setArchiveFromFilter('');
      setArchiveToFilter('');
      setFilter((state) => ({
        ...state,
        archiveFrom: '',
        archiveTo: '',
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              archiveFrom: '',
              archiveTo: '',
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [sourceSystemTypeFilter],
  );

  const handleUncheckState = useCallback(
    (value: string) => {
      let valId = 0;
      switch (value) {
        case 'Wszystkie sesje zamknięte':
          valId = 2;
          break;
        case 'Nie wszystkie sesje zamknięte':
          valId = 1;
          break;
      }
      const val = stateFilter.filter((x) => x !== valId.toString());
      setStateFilter(val);
      setFilter((state) => ({
        ...state,
        state: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              state: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [stateFilter],
  );

  const handleUncheckStatus = useCallback(
    (value: string) => {
      const val = isConfiguredFilter.some((f) => ExamSheetConfigured[parseInt(f) - 1] === value)
        ? isConfiguredFilter.filter((f) => parseInt(f) !== ExamSheetConfigured.indexOf(value) + 1)
        : [...isConfiguredFilter, (ExamSheetConfigured.indexOf(value) + 1).toString()];
      setIsConfiguredFilter(val);
      setFilter((state) => ({
        ...state,
        isConfigured: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              isConfigured: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [isConfiguredFilter],
  );

  const handleFilterChange = useCallback(
    (name: string, value: string | string[]) => {
      typeof value === 'string' && value.length === 1 ? setIsValidFilter(false) : setIsValidFilter(true);
      if (filter[name] === value) return;

      setFilter((state) => {
        if (state[name] !== value) {
          return {
            ...state,
            [name]: value,
          };
        } else {
          return state;
        }
      });
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              [name]: value,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
      const newValue = value;
      if (value && typeof value === 'string') {
        if (name === 'month') {
          setMonthFilter((arr: string[]) => _.uniq([...arr, ...newValue]));
        }
        if (name === 'sourceSystemType') {
          setSourceSystemTypeFilter((arr: string[]) => _.uniq([...arr, ...newValue]));
        }
        if (name === 'isConfigured') {
          setIsConfiguredFilter((arr: string[]) => _.uniq([...arr, ...newValue]));
        }
        if (name === 'archiveFromFilter') {
          const newVal = newValue as string;
          setArchiveFromFilter(newVal);
        }
        if (name === 'archiveToFilter') {
          const newVal = newValue as string;
          setArchiveToFilter(newVal);
        }
        if (name === 'state') {
          setStateFilter((arr: string[]) => _.uniq([...arr, ...newValue]));
        }
      } else if (value) {
        if (name === 'month') {
          setMonthFilter([...newValue]);
        }
        if (name === 'sourceSystemType') {
          setSourceSystemTypeFilter([...newValue]);
        }
        if (name === 'isConfigured') {
          setIsConfiguredFilter([...newValue]);
        }
        if (name === 'state') {
          setStateFilter([...newValue]);
        }
      }
    },
    [monthFilter, sourceSystemTypeFilter, isConfiguredFilter, stateFilter, archiveFromFilter, archiveToFilter, filter],
  );

  const uncheckFilterItem = useCallback(
    (item?: string) => {
      if (item) {
        const element = filterState.find((e) => e.checkedItems.find((x) => x === item));
        if (element?.name) {
          if (element.name === 'Miesiąc:') {
            handleUncheckMonthItem(item);
          }
          if (element.name === 'System źródłowy:') {
            handleUncheckSourceSystemTypeItem(item);
          }
          if (element.name === 'Konfiguracja arkusza:') {
            handleUncheckStatus(item);
          }
          if (element.name === 'Archiwum:') {
            handleUncheckArchiveItem(item);
          }
          if (element.name === 'Stan:') {
            handleUncheckState(item);
          }
        }
      }
    },
    [
      filterState,
      handleUncheckArchiveItem,
      handleUncheckMonthItem,
      handleUncheckSourceSystemTypeItem,
      handleUncheckState,
      handleUncheckStatus,
    ],
  );

  const setIsDescendingOrder = useCallback(
    (order: boolean) => {
      setIsDescending(order);
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: { sort: { isDescending: order } },
        }),
      );
    },
    [dataInfoType, dispatch],
  );

  const setSortByColumn = useCallback(
    (column: SessionSortBy) => {
      setSortByColumnId(column);
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: { sort: { sortByColumnId: column } },
        }),
      );
    },
    [dataInfoType, dispatch],
  );

  return {
    sessionId,
    filter,
    pageInfo: sessionsDataInfo.filterData.pageInfo,
    sessions,
    selectedSessionId,
    setSelectedSessionId,
    selectedSoureSystem,
    setSelectedSourceSystem,
    selectedSheetCode,
    setSelectedSheetCode,
    selectedSheetId,
    setSelectedSheetId,
    selectedSystem,
    setSelectedSystem,
    getSessions,
    handleFilterChange,
    getPages,
    uncheckFilterItem,
    uncheckAllFilters,
    sortByColumnId,
    setSortByColumnId: setSortByColumn,
    isDescending,
    setIsDescending: setIsDescendingOrder,
    filterState,
  };
};
